import { uniqueId } from 'lodash';

import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { Person } from '../../redux/Candidate/Candidate.type';
import { PersonLink } from '../../redux/Candidate/Link/PersonLink.type';
import { PersonExperience } from '../../redux/Candidate/Experience/PersonExperience.type';
import { Tag } from '../../redux/ControlPanel/TagControl/TagControl.type';
import EmailIcon from '../Icons/Email.icon';
import PhoneIcon from '../Icons/Phone.icon';
import { PERSON_LINKS_ICONS } from '../Icons/PersonLinks.icons';
import BriefcaseIcon from '../Icons/Briefcase.icon';
import TagBlock from './TagBlock';
import { Region } from '../../redux/Region/Region.type';
import { EnglishLevel } from '../../redux/Candidate/English/EnglishLevel.type';
import InfoIcon from '../Icons/Info.icon';

export interface CandidateInfoBlockProps {
  info: Person;
}

const StyledCandidateInfoRowR = styled.div`
  max-width: 200px;
  word-break: break-word;
`;
const StyledCandidateInfoRowL = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 100px;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledCandidateInfoRowLabel = styled.div``;
const StyledCandidateInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  font-size: 12px;
  line-height: 14px;
  font-weight: 500;
`;
const StyledCandidateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;
const StyledUnreachableRegionsVarning = styled.div`
  color: ${({ theme }) => theme.colors.STATUSES_RED};
  max-width: 250px;
`;

const INFO_ICON_SIZE = 14;

const CandidateInfoBlockComponent = ({
  links,
  experience,
  email,
  phone,
  tags,
  metaRegions,
  english,
  hasUnreachableRegions,
  hasUnreachableRegionsVarning,
}: {
  links: PersonLink[] | null;
  experience: PersonExperience | null;
  email: string | null;
  phone: string | null;
  tags: Tag[] | null;
  metaRegions: Region[] | null;
  english: EnglishLevel | null;
  hasUnreachableRegions: boolean | null;
  hasUnreachableRegionsVarning: boolean | null;
}) => {
  const { t } = useTranslation();
  const theme = useTheme() as any;

  const renderLinks = () => {
    let linksWithLinkedin =
      links === null || links.length === 0 ? [] : [...links];

    if (links === null || links.length === 0) {
      linksWithLinkedin = [
        { type: 'LINKEDIN', display: '-', uuid: '', link: '', linkRaw: '' },
      ];
    } else {
      const isLinkedinLink = links.find(i => i.type === 'LINKEDIN');
      linksWithLinkedin = isLinkedinLink
        ? [...links]
        : [
            ...links,
            { type: 'LINKEDIN', display: '-', uuid: '', link: '', linkRaw: '' },
          ];
    }

    return linksWithLinkedin.map((link: PersonLink) => (
      <StyledCandidateInfoRow key={uniqueId('candidate-info-link-')}>
        <StyledCandidateInfoRowL>
          {PERSON_LINKS_ICONS[link.type]}
          <StyledCandidateInfoRowLabel>
            {t(link.type)}:
          </StyledCandidateInfoRowLabel>
        </StyledCandidateInfoRowL>
        <StyledCandidateInfoRowR>
          {link.link ? (
            <a href={link.link} target="_blank">
              {link.display}
            </a>
          ) : (
            '-'
          )}
        </StyledCandidateInfoRowR>
      </StyledCandidateInfoRow>
    ));
  };

  return (
    <StyledCandidateInfo>
      <StyledCandidateInfoRow>
        <StyledCandidateInfoRowL>
          <EmailIcon width={INFO_ICON_SIZE} height={INFO_ICON_SIZE} />
          <StyledCandidateInfoRowLabel>
            {t('Email')}:
          </StyledCandidateInfoRowLabel>
        </StyledCandidateInfoRowL>
        <StyledCandidateInfoRowR>
          {email ? (
            <a href={`mailto:${email}`} target="_blank">
              {email}
            </a>
          ) : (
            '-'
          )}
        </StyledCandidateInfoRowR>
      </StyledCandidateInfoRow>

      <StyledCandidateInfoRow>
        <StyledCandidateInfoRowL>
          <PhoneIcon width={INFO_ICON_SIZE} height={INFO_ICON_SIZE} />
          <StyledCandidateInfoRowLabel>
            {t('Phone')}:
          </StyledCandidateInfoRowLabel>
        </StyledCandidateInfoRowL>
        <StyledCandidateInfoRowR>{phone || '-'}</StyledCandidateInfoRowR>
      </StyledCandidateInfoRow>

      <StyledCandidateInfoRow>
        <StyledCandidateInfoRowL>
          <BriefcaseIcon width={INFO_ICON_SIZE} height={INFO_ICON_SIZE} />
          <StyledCandidateInfoRowLabel>
            {t('Experience')}:
          </StyledCandidateInfoRowLabel>
        </StyledCandidateInfoRowL>
        <StyledCandidateInfoRowR>
          {experience?.name || '-'}
        </StyledCandidateInfoRowR>
      </StyledCandidateInfoRow>

      <StyledCandidateInfoRow>
        <StyledCandidateInfoRowL>
          <StyledCandidateInfoRowLabel>
            {t('English level')}:
          </StyledCandidateInfoRowLabel>
        </StyledCandidateInfoRowL>
        <StyledCandidateInfoRowR>
          {!!english?.level ? (
            <StyledCandidateInfoRowLabel>
              {english.level}
            </StyledCandidateInfoRowLabel>
          ) : (
            '-'
          )}
        </StyledCandidateInfoRowR>
      </StyledCandidateInfoRow>

      <StyledCandidateInfoRow>
        <StyledCandidateInfoRowL>
          <StyledCandidateInfoRowLabel>
            {t('Region')}:
          </StyledCandidateInfoRowLabel>
        </StyledCandidateInfoRowL>
        <StyledCandidateInfoRowR>
          {!!metaRegions && metaRegions.length
            ? metaRegions.map((region: Region) => (
                <StyledCandidateInfoRowLabel key={region.id}>
                  {region.name}
                </StyledCandidateInfoRowLabel>
              ))
            : '-'}
        </StyledCandidateInfoRowR>
      </StyledCandidateInfoRow>

      {hasUnreachableRegions && !hasUnreachableRegionsVarning && (
        <StyledCandidateInfoRow>
          <StyledCandidateInfoRowL
            title={t(
              'controlModal.region.Attention! This candidate was found in the partner companys system',
            )}
            style={{ cursor: 'pointer' }}
          >
            <StyledCandidateInfoRowL
              style={{
                border: `1px solid ${theme.colors.BLUE.B400}`,
                padding: '4px 8px',
              }}
            >
              <InfoIcon
                width={INFO_ICON_SIZE}
                height={INFO_ICON_SIZE}
                color={theme.colors.BLUE.B400}
              />
              <StyledCandidateInfoRowLabel
                style={{
                  color: theme.colors.BLUE.B400,
                }}
              >
                {t('Info')}
              </StyledCandidateInfoRowLabel>
            </StyledCandidateInfoRowL>
          </StyledCandidateInfoRowL>
        </StyledCandidateInfoRow>
      )}

      {hasUnreachableRegions && hasUnreachableRegionsVarning && (
        <StyledUnreachableRegionsVarning>
          {t(
            'controlModal.region.Attention! This candidate was found in the partner companys system',
          )}
        </StyledUnreachableRegionsVarning>
      )}

      {renderLinks()}
      <StyledCandidateInfoRow>
        <StyledCandidateInfoRowL>
          <StyledCandidateInfoRowLabel>
            {t('Tags')}:
          </StyledCandidateInfoRowLabel>
        </StyledCandidateInfoRowL>
        <StyledCandidateInfoRowR>
          {!!tags && tags.length > 0 ? <TagBlock value={tags} /> : '-'}
        </StyledCandidateInfoRowR>
      </StyledCandidateInfoRow>
    </StyledCandidateInfo>
  );
};

export default CandidateInfoBlockComponent;
